import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import pdfjsLib from '../utils/pdfjs';

function PdfThumbnail({ url, className = '', onClick }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const renderPdf = async () => {
      if (!url || !canvasRef.current) return;

      try {
        setLoading(true);
        setError(false);

        // Load the PDF document
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;

        if (cancelled) return;

        // Get the first page
        const page = await pdf.getPage(1);

        if (cancelled) return;

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Calculate scale to fit thumbnail
        const desiredHeight = 192; // h-48 = 192px
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredHeight / viewport.height;
        const scaledViewport = page.getViewport({ scale });

        // Set canvas dimensions
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        // Render the page
        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
        }).promise;

        setLoading(false);
      } catch (err) {
        console.error('Error rendering PDF thumbnail:', err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    // Fallback to icon when PDF can't be rendered
    return (
      <div 
        className={`w-full h-full flex flex-col items-center justify-center bg-gray-200 ${className}`}
        onClick={onClick}
      >
        <FileText className="w-12 h-12 text-red-500 mb-2" />
        <p className="text-xs text-gray-700">PDF</p>
      </div>
    );
  }

  return (
    <div 
      className={`w-full h-full flex items-center justify-center bg-gray-100 ${className}`}
      onClick={onClick}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={`max-w-full max-h-full object-contain ${loading ? 'opacity-0' : 'opacity-100'}`}
      />
    </div>
  );
}

export default PdfThumbnail;
